import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { ContractorReferralForm } from '../components/ContractorReferralForm';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  Users, Hammer, Ticket, ShieldCheck, Copy, Mail, MessageSquare,
  Share2, Trophy, ArrowRight, MapPin, RefreshCw, Home, HeartHandshake, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700',
  verified: 'bg-green-100 text-green-700',
  rewarded: 'bg-green-100 text-green-700',
  duplicate: 'bg-neutral-200 text-neutral-600',
  ineligible: 'bg-neutral-200 text-neutral-600',
  rejected: 'bg-red-100 text-red-700',
  under_review: 'bg-blue-100 text-blue-700',
};

const StatusBadge = ({ status }) => (
  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[status] || 'bg-neutral-100 text-neutral-600'}`}>
    {(status || 'pending').replace(/_/g, ' ')}
  </span>
);

export default function AffiliateLanding() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [showContractorForm, setShowContractorForm] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const lb = await axios.get(`${API}/referral-program/leaderboard`);
      setLeaderboard(lb.data);
    } catch (e) { /* leaderboard optional */ }
    if (isAuthenticated) {
      try {
        const res = await axios.get(`${API}/referral-program/my-stats`);
        setStats(res.data);
      } catch (e) { /* not critical */ }
    }
  }, [isAuthenticated]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const referralLink = stats?.referral_code
    ? `${window.location.origin}/?ref=${stats.referral_code}`
    : null;

  const copyLink = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink);
    toast.success('Your personal referral link has been copied.');
    document.getElementById('referral-dashboard')?.scrollIntoView({ behavior: 'smooth' });
  };

  const heroPrimary = () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!referralLink) {
      toast.info('Complete a free giveaway entry to create your personal referral link.');
      navigate('/giveaways');
      return;
    }
    copyLink();
  };

  const shareText = encodeURIComponent(`Enter Spinser's free home improvement giveaways with my link: ${referralLink}`);
  const pct = stats ? Math.min(100, (stats.verified_homeowner_referrals % stats.referrals_per_entry) / stats.referrals_per_entry * 100) : 0;
  const inCycle = stats ? stats.verified_homeowner_referrals % stats.referrals_per_entry : 0;
  const refPriority = { rewarded: 0, pending: 1, verified_no_reward: 2, duplicate: 3, ineligible: 3, rejected: 4 };
  const contractorRef = [...(stats?.contractor_referrals || [])]
    .sort((a, b) => (refPriority[a.reward_status] ?? 5) - (refPriority[b.reward_status] ?? 5))[0];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* 1. HERO */}
      <section className="pt-28 pb-16 bg-neutral-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <p className="text-[#FF3B30] font-semibold uppercase tracking-widest text-sm mb-4">Spinser Referral Rewards</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'Outfit' }}>
            Share Spinser. <span className="text-[#FF3B30]">Earn More Chances to Win.</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
            Invite homeowners to enter Spinser giveaways and recommend contractors whose workmanship you trust.
            Verified referrals can earn you additional giveaway entries.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="hero-primary-btn" onClick={heroPrimary} className="h-14 px-6 sm:px-8 bg-[#FF3B30] hover:bg-[#DC2626] text-white text-base sm:text-lg rounded-xl font-medium whitespace-nowrap">
              {referralLink ? 'Copy My Referral Link' : 'Get Your Link'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button data-testid="hero-secondary-btn" variant="outline" onClick={() => isAuthenticated ? document.getElementById('referral-dashboard')?.scrollIntoView({ behavior: 'smooth' }) : navigate('/login')}
              className="h-14 px-8 border-white/30 bg-transparent text-white hover:bg-white/10 text-lg rounded-xl">
              View Your Referrals
            </Button>
          </div>
          <p className="text-white/60 text-sm mt-4 max-w-xl mx-auto" data-testid="hero-supporting-text">
            Create your personal referral link and share it with homeowners. Spinser will track verified entries completed through your link.
          </p>
          <p className="text-white/40 text-xs mt-6" data-testid="hero-trust-line">
            No purchase necessary · Verified referrals only · Referral limits apply · <Link to="/terms" className="underline hover:text-white/70">See Official Rules</Link>
          </p>
          <div className="mt-10 inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2">
            <MapPin className="w-4 h-4 text-[#FF3B30]" />
            <span className="text-sm text-white/80">Launching first in Halton Hills, Ontario</span>
          </div>
          <p className="text-white/40 text-sm mt-3">Join Spinser's founding community of homeowners, contractors and local sponsors.</p>
        </div>
      </section>

      {/* 2. REWARD SUMMARY */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            data-testid="reward-card-homeowners" className="bg-white rounded-2xl border border-neutral-200 p-8 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center"><Users className="w-6 h-6 text-[#FF3B30]" /></div>
              <span className="bg-[#FF3B30] text-white text-xs font-bold px-3 py-1.5 rounded-full">1 Additional Entry</span>
            </div>
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Refer Homeowners</h3>
            <p className="text-neutral-600 mb-3">
              Receive 1 additional giveaway entry for every 50 eligible homeowners who complete a verified Spinser entry through your unique referral link.
            </p>
            <p className="text-xs text-neutral-400 mb-5">
              Link clicks alone do not count. The referred homeowner must complete an eligible entry and pass Spinser's verification checks.
            </p>
            <div className="mt-auto">
              <div className="flex justify-between text-sm mb-1.5">
                <span className="font-medium text-neutral-700">{stats ? `${inCycle} of ${stats.referrals_per_entry} verified homeowners` : 'Example: 34 of 50 verified homeowners'}</span>
              </div>
              <div className="h-2.5 bg-neutral-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#FF3B30] rounded-full transition-all" style={{ width: `${stats ? Math.max(2, pct) : 68}%` }} />
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                {stats
                  ? (stats.remaining_to_next_entry > 0 ? `${stats.remaining_to_next_entry} more verified homeowner referrals to unlock your next additional entry.` : 'Maximum homeowner-referral entries reached for this giveaway.')
                  : '16 more verified homeowner referrals to unlock your next additional entry.'}
              </p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} viewport={{ once: true }}
            data-testid="reward-card-contractor" className="bg-neutral-900 rounded-2xl p-8 text-white flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-[#FF3B30]/20 rounded-xl flex items-center justify-center"><Hammer className="w-6 h-6 text-[#FF3B30]" /></div>
              <span className="bg-[#FF3B30] text-white text-xs font-bold px-3 py-1.5 rounded-full">5 Additional Entries</span>
            </div>
            <h3 className="text-xl font-bold mb-3">Recommend a Trusted Contractor</h3>
            <p className="text-white/70 mb-3">
              Recommend a contractor whose workmanship you have personally experienced and would confidently recommend.
              Receive 5 additional giveaway entries when the contractor completes registration and is verified by Spinser.
            </p>
            <p className="text-xs text-white/40 mb-5">
              The contractor must be a legitimate home-service business, must not already be registered or previously rewarded through another referral, and must complete Spinser's required verification. Limit: one rewarded contractor referral per homeowner, per giveaway.
            </p>
            <Button data-testid="recommend-contractor-btn" onClick={() => isAuthenticated ? setShowContractorForm((s) => !s) : navigate('/login')}
              className="mt-auto h-12 bg-white text-neutral-900 hover:bg-neutral-100 rounded-xl font-medium">
              Recommend a Contractor
            </Button>
          </motion.div>
        </div>
        {showContractorForm && (
          <div className="max-w-3xl mx-auto px-4 sm:px-8 mt-8">
            <div className="bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8">
              <h3 className="text-lg font-bold text-neutral-900 mb-4">Contractor Recommendation</h3>
              <ContractorReferralForm onSubmitted={() => { setShowContractorForm(false); fetchData(); }} />
            </div>
          </div>
        )}
      </section>

      {/* 3. HOW IT WORKS */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <h2 className="text-3xl font-bold text-neutral-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Ticket, title: 'Enter for Free', text: 'Complete an eligible entry for an available Spinser home-improvement giveaway.' },
              { icon: Share2, title: 'Share and Recommend', text: 'Share your unique referral link with homeowners and recommend a contractor whose work you have personally used and liked.' },
              { icon: Sparkles, title: 'Earn Additional Entries', text: 'Receive 1 additional entry for every 50 verified homeowner referrals and 5 additional entries for one eligible, verified contractor referral.' },
            ].map((s, i) => (
              <div key={s.title} data-testid={`how-step-${i}`} className="text-center">
                <div className="w-14 h-14 bg-[#FF3B30]/10 rounded-2xl flex items-center justify-center mx-auto mb-4"><s.icon className="w-7 h-7 text-[#FF3B30]" /></div>
                <p className="text-xs font-bold text-neutral-400 mb-1">STEP {i + 1}</p>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">{s.title}</h3>
                <p className="text-neutral-600 text-sm">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. USER REFERRAL DASHBOARD */}
      {isAuthenticated && stats && (
        <section id="referral-dashboard" className="py-16 bg-neutral-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-8">
            <h2 className="text-3xl font-bold text-neutral-900 mb-8" style={{ fontFamily: 'Outfit' }}>My Referral Dashboard</h2>

            {referralLink ? (
              <div className="bg-white rounded-2xl border border-neutral-200 p-6 mb-6">
                <div className="flex flex-col lg:flex-row gap-6 items-start">
                  <div className="flex-1 w-full">
                    <p className="font-semibold text-neutral-900 mb-1">Your Personal Referral Link</p>
                    <p className="text-xs text-neutral-500 mb-3">
                      Share this link with eligible homeowners. Verified giveaway entries completed through your link will count toward your referral progress.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <code data-testid="referral-link" className="flex-1 min-w-[200px] bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-700 truncate">{referralLink}</code>
                      <Button data-testid="copy-link-btn" onClick={copyLink} className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl"><Copy className="w-4 h-4 mr-2" />Copy</Button>
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <a data-testid="share-sms-btn" href={`sms:?body=${shareText}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-neutral-200 text-sm hover:border-neutral-400"><MessageSquare className="w-4 h-4" />Text</a>
                      <a data-testid="share-email-btn" href={`mailto:?subject=${encodeURIComponent('Free home upgrade giveaways on Spinser')}&body=${shareText}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-neutral-200 text-sm hover:border-neutral-400"><Mail className="w-4 h-4" />Email</a>
                      <a data-testid="share-facebook-btn" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-neutral-200 text-sm hover:border-neutral-400"><Share2 className="w-4 h-4" />Facebook</a>
                      <a data-testid="share-whatsapp-btn" href={`https://wa.me/?text=${shareText}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-neutral-200 text-sm hover:border-neutral-400"><MessageSquare className="w-4 h-4" />WhatsApp</a>
                    </div>
                  </div>
                  <div className="text-center shrink-0">
                    <img data-testid="referral-qr" src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(referralLink)}`} alt="QR code for your referral link" className="rounded-xl border border-neutral-200" />
                    <p className="text-xs text-neutral-400 mt-2">Scan to share</p>
                  </div>
                </div>
                <p className="text-xs text-neutral-400 mt-4 border-t border-neutral-100 pt-3" data-testid="link-clarification">
                  Link visits do not count as verified referrals. A referred homeowner must complete an eligible Spinser giveaway entry through your personal referral link.
                </p>
              </div>
            ) : (
              <div data-testid="no-link-prompt" className="bg-white rounded-2xl border border-neutral-200 p-8 mb-6 text-center">
                <p className="font-semibold text-neutral-900 mb-2">Create your personal referral link</p>
                <p className="text-sm text-neutral-500 mb-5 max-w-md mx-auto">
                  Complete an eligible giveaway entry to create your personal referral link. Spinser will track verified entries completed through your link.
                </p>
                <Button data-testid="no-link-enter-btn" onClick={() => navigate('/giveaways')} className="h-12 px-8 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium">
                  Create My Personal Referral Link
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                ['Link visits', stats.total_clicks, 'stat-clicks'],
                ['Verified referrals', stats.verified_homeowner_referrals, 'stat-verified'],
                ['Pending referrals', stats.pending_homeowner_referrals, 'stat-pending'],
                ['Total additional entries', stats.total_additional_entries, 'stat-total-entries'],
              ].map(([label, value, tid]) => (
                <div key={tid} data-testid={tid} className="bg-white rounded-2xl border border-neutral-200 p-5 text-center">
                  <p className="text-3xl font-bold text-neutral-900">{value}</p>
                  <p className="text-xs text-neutral-500 mt-1">{label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div data-testid="homeowner-progress-module" className="bg-white rounded-2xl border border-neutral-200 p-6">
                <div className="flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-[#FF3B30]" /><h3 className="font-semibold text-neutral-900">Homeowner Referrals</h3></div>
                <p className="text-2xl font-bold text-neutral-900 mb-2">{inCycle} of {stats.referrals_per_entry} verified</p>
                <div className="h-2.5 bg-neutral-100 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-[#FF3B30] rounded-full" style={{ width: `${Math.max(2, pct)}%` }} />
                </div>
                <p className="text-sm text-neutral-500">
                  {stats.remaining_to_next_entry > 0
                    ? `${stats.remaining_to_next_entry} more verified referrals to unlock your next additional entry.`
                    : 'Maximum additional entries reached for this giveaway.'}
                </p>
                <p className="text-xs text-neutral-400 mt-2">Earned so far: {stats.homeowner_entries_earned} of {stats.max_homeowner_entries} max additional entries</p>
              </div>

              <div data-testid="contractor-module" className="bg-white rounded-2xl border border-neutral-200 p-6">
                <div className="flex items-center gap-2 mb-3"><Hammer className="w-5 h-5 text-[#FF3B30]" /><h3 className="font-semibold text-neutral-900">Trusted Contractor Referral</h3></div>
                {!contractorRef && (
                  <>
                    <p className="text-lg font-bold text-neutral-400 mb-2">Not completed</p>
                    <p className="text-sm text-neutral-500 mb-4">Recommend an eligible contractor to unlock 5 additional entries.</p>
                    <Button size="sm" onClick={() => setShowContractorForm(true)} className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-xl">Recommend a Contractor</Button>
                  </>
                )}
                {contractorRef && contractorRef.reward_status === 'pending' && (
                  <>
                    <div className="flex items-center gap-2 mb-2"><StatusBadge status="pending" /><span className="font-semibold text-neutral-900">Contractor verification in progress</span></div>
                    <p className="text-sm text-neutral-500">Your entries will be awarded after the contractor completes registration and is approved.</p>
                  </>
                )}
                {contractorRef && contractorRef.reward_status === 'rewarded' && (
                  <>
                    <div className="flex items-center gap-2 mb-2"><StatusBadge status="verified" /><span className="font-semibold text-neutral-900">Contractor verified</span></div>
                    <p className="text-sm text-green-700 font-medium">{contractorRef.reward_entries_awarded} additional entries awarded.</p>
                  </>
                )}
                {contractorRef && ['ineligible', 'duplicate', 'verified_no_reward', 'rejected'].includes(contractorRef.reward_status) && (
                  <>
                    <div className="flex items-center gap-2 mb-2"><StatusBadge status={contractorRef.reward_status} /><span className="font-semibold text-neutral-900">{contractorRef.contractor_name}</span></div>
                    <p className="text-sm text-neutral-500">{contractorRef.rejection_reason || 'This recommendation is not eligible for reward entries.'}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 5. LEADERBOARD */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Trophy className="w-6 h-6 text-[#FF3B30]" />
            <h2 className="text-3xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>Current Giveaway Referral Leaders</h2>
          </div>
          {leaderboard?.leaders?.length > 0 ? (
            <div data-testid="referral-leaderboard" className="bg-neutral-50 rounded-2xl border border-neutral-200 divide-y divide-neutral-200 text-left">
              {leaderboard.leaders.map((l) => (
                <div key={l.rank} className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-neutral-900 text-white text-sm font-bold flex items-center justify-center">{l.rank}</span>
                    <span className="font-medium text-neutral-900">{l.name}</span>
                  </div>
                  <span className="text-sm text-neutral-500">{l.verified_referrals} verified referral{l.verified_referrals !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <div data-testid="leaderboard-empty-state" className="bg-neutral-50 rounded-2xl border border-neutral-200 p-10">
              <p className="text-lg font-semibold text-neutral-900 mb-2">The referral leaderboard will begin when the first eligible giveaway launches.</p>
              <p className="text-neutral-500">Enter, share your unique link and become one of Spinser's founding referrers.</p>
            </div>
          )}
        </div>
      </section>

      {/* 6. ELIGIBILITY & VERIFICATION */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className="w-6 h-6 text-[#FF3B30]" />
            <h2 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>What Counts as a Verified Referral?</h2>
          </div>
          <Accordion type="single" collapsible className="bg-white rounded-2xl border border-neutral-200 px-6">
            <AccordionItem value="homeowner">
              <AccordionTrigger data-testid="accordion-homeowner" className="font-semibold">Homeowner referral requirements</AccordionTrigger>
              <AccordionContent className="text-neutral-600 text-sm space-y-2">
                <p>A homeowner referral counts only when:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>The person uses your unique referral link.</li>
                  <li>The person is a new, eligible participant.</li>
                  <li>The person completes a valid giveaway entry.</li>
                  <li>The person meets the applicable age and geographic requirements.</li>
                  <li>The entry passes duplicate, identity and fraud checks.</li>
                  <li>The referral is not a self-referral.</li>
                  <li>The person has not already registered through another referral source.</li>
                </ul>
                <p className="font-medium text-neutral-800">Clicks, incomplete registrations, duplicate accounts, bots, false information and self-referrals do not count.</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="contractor">
              <AccordionTrigger data-testid="accordion-contractor" className="font-semibold">Contractor referral requirements</AccordionTrigger>
              <AccordionContent className="text-neutral-600 text-sm space-y-2">
                <p>A contractor referral qualifies only when:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>You have personally hired, worked with or directly experienced the contractor's workmanship.</li>
                  <li>The contractor provides an eligible home-related service.</li>
                  <li>The business is legitimate and provides accurate contact information.</li>
                  <li>The contractor completes the required Spinser registration.</li>
                  <li>The contractor confirms or acknowledges the referral.</li>
                  <li>The contractor passes Spinser's initial verification.</li>
                  <li>The contractor is not already registered.</li>
                  <li>The contractor has not already generated a referral reward for another participant.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* 7. FLYWHEEL */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-3xl font-bold text-neutral-900 mb-12" style={{ fontFamily: 'Outfit' }}>Every Referral Helps Build the Spinser Community</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Home, title: 'Homeowners Share', text: 'More eligible homeowners discover free Spinser giveaways.' },
              { icon: Hammer, title: 'Trusted Contractors Are Recommended', text: 'Homeowners help Spinser discover quality local contractors based on firsthand experience.' },
              { icon: HeartHandshake, title: 'Sponsors Fund Projects', text: 'Realtors, contractors and brands collaborate to fund meaningful home improvements.' },
              { icon: Sparkles, title: 'Real Projects Create Real Impact', text: 'Winners receive completed work, contractors gain opportunities and sponsors receive authentic local exposure.' },
            ].map((s, i) => (
              <div key={s.title} data-testid={`flywheel-stage-${i}`} className="relative bg-neutral-50 rounded-2xl border border-neutral-200 p-6">
                <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center mx-auto mb-4"><s.icon className="w-6 h-6 text-[#FF3B30]" /></div>
                <h3 className="font-semibold text-neutral-900 mb-2 text-sm">{s.title}</h3>
                <p className="text-neutral-500 text-xs leading-relaxed">{s.text}</p>
                {i < 3 && <ArrowRight className="hidden lg:block absolute top-1/2 -right-5 -translate-y-1/2 w-4 h-4 text-neutral-300" />}
              </div>
            ))}
          </div>
          <p className="text-neutral-500 mt-8 font-medium">Spinser grows through trusted local recommendations—not purchased lead lists.</p>
        </div>
      </section>

      {/* 9. LEGAL & TRUST */}
      <section className="py-12 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <p className="text-xs text-neutral-500 leading-relaxed mb-3" data-testid="legal-trust-copy">
            No purchase necessary. Additional entries are awarded only for eligible, verified referrals and are subject to
            the limits stated in each giveaway's Official Rules. Eligibility, prize details, geographic restrictions,
            entry periods, referral limits and winner-selection procedures may vary by giveaway.
          </p>
          <p className="text-xs text-neutral-500 mb-5">
            Canadian winners may be required to correctly answer a mathematical skill-testing question before being confirmed as a winner.
          </p>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs">
            <Link to="/terms" className="text-[#FF3B30] hover:underline">Official Rules</Link>
            <Link to="/terms" className="text-[#FF3B30] hover:underline">Terms and Conditions</Link>
            <Link to="/privacy" className="text-[#FF3B30] hover:underline">Privacy Policy</Link>
            <Link to="/privacy#cookie-policy" className="text-[#FF3B30] hover:underline">Cookie Policy</Link>
            <Link to="/terms" className="text-[#FF3B30] hover:underline">Referral Program Terms</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
